// First-touch landing-cluster attribution.
//
// Why this exists: v_seo_funnel.sql buckets sessions into landing_cluster
// (kundli/dosha/panchang/festival/muhurat/matching/rashifal/product/home/other)
// derived from page_path. But paid Razorpay orders all land in '_unattributed'
// because by the time the user reaches checkout (chat → paywall → Razorpay
// modal), the current path is /ai-astrologer/ or /, not the SEO page that
// drove discovery.
//
// Pattern: on first page load in a tab, derive the cluster from the actual
// landing URL and persist it in sessionStorage. Subsequent payment-order
// creates thread it through to Razorpay notes so the order can be attributed
// back to the SEO cluster that earned the visit.

import { safeSessionGet, safeSessionSet } from "@/lib/browser-storage";
import {
  REDIRECT_LANDING_COOKIE,
  normalizedLandingPath,
  shouldPersistLandingPath,
} from "@/lib/landing-attribution-policy";

const STORAGE_KEY = "ish_landing_cluster_v1";
const PATH_STORAGE_KEY = "ish_landing_path_v1";

export type LandingCluster =
  | "kundli"
  | "matching"
  | "dosha"
  | "panchang"
  | "muhurat"
  | "rashifal"
  | "festival"
  | "product"
  | "home"
  | "other";

// Mirror of v_seo_funnel.sql page_path classification. Keep these in sync —
// changing one without the other will desynchronise the funnel.
const CLUSTER_RULES: { matcher: RegExp; cluster: LandingCluster }[] = [
  { matcher: /^\/(hi\/)?products?\//i, cluster: "product" },
  { matcher: /^\/(hi\/)?kundli-matching/i, cluster: "matching" },
  { matcher: /^\/(hi\/)?gun-milan/i, cluster: "matching" },
  { matcher: /^\/(hi\/)?(free-)?kundali/i, cluster: "kundli" },
  { matcher: /^\/(hi\/)?(free-)?kundli/i, cluster: "kundli" },
  { matcher: /^\/(hi\/)?dosha/i, cluster: "dosha" },
  { matcher: /^\/(hi\/)?panchang/i, cluster: "panchang" },
  { matcher: /^\/(hi\/)?muhurat/i, cluster: "muhurat" },
  { matcher: /^\/(hi\/)?(aaj-ka-)?rashifal/i, cluster: "rashifal" },
  { matcher: /^\/(hi\/)?(daily-)?horoscope/i, cluster: "rashifal" },
  { matcher: /-(20\d{2})\/?$/, cluster: "festival" },
];

function isHomePath(path: string): boolean {
  return path === "/" || path === "/hi" || path === "/hi/";
}

export function classifyPath(pathname: string): LandingCluster {
  const path = pathname.split("?")[0].split("#")[0] || "/";
  if (isHomePath(path)) return "home";
  for (const { matcher, cluster } of CLUSTER_RULES) {
    if (matcher.test(path)) return cluster;
  }
  return "other";
}

function redirectLandingCookie(): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${REDIRECT_LANDING_COOKIE}=`;
  const pair = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!pair) return null;
  try {
    return normalizedLandingPath(decodeURIComponent(pair.slice(prefix.length)));
  } catch {
    return null;
  }
}

function clearRedirectLandingCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${REDIRECT_LANDING_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}

/**
 * Persist the current pathname as the first-touch landing for this session
 * if not already set. Safe to call multiple times — only the first call wins.
 * Returns the persisted cluster (existing or just-stored).
 */
export function captureFirstTouchLanding(pathname?: string): LandingCluster {
  const existing = safeSessionGet(STORAGE_KEY) as LandingCluster | null;
  if (existing) {
    clearRedirectLandingCookie();
    return existing;
  }

  const path = normalizedLandingPath(
    redirectLandingCookie() ??
      pathname ??
      (typeof window !== "undefined" ? window.location.pathname : "/")
  );
  const cluster = classifyPath(path);

  if (!shouldPersistLandingPath(path)) {
    clearRedirectLandingCookie();
    return cluster;
  }

  safeSessionSet(STORAGE_KEY, cluster);
  safeSessionSet(PATH_STORAGE_KEY, path);
  clearRedirectLandingCookie();
  return cluster;
}

/**
 * Read the persisted first-touch cluster without writing. Returns null if
 * the user landed before this helper started running (graceful degradation).
 */
export function getFirstTouchLanding(): {
  cluster: LandingCluster | null;
  path: string | null;
} {
  return {
    cluster: (safeSessionGet(STORAGE_KEY) as LandingCluster | null) ?? null,
    path: safeSessionGet(PATH_STORAGE_KEY),
  };
}
